// routing
import { withRouter } from './withRouter';
import { Navigate, Route, Routes } from 'react-router';
import { BrowserRouter, useParams, useLocation } from 'react-router-dom';
import React, { useLayoutEffect } from 'react';
import URLS from './ApiURLs';
import ProfileStore from '../stores/ProfileStore';
import Config from 'arbitrip-common/client/utils/Config';
import { addUtmSourceToUrl } from 'arbitrip-common/client/utils/UtmUtils';

import ManagementConstants from '../constants/ManagementConstants';

import ScrollToTop from '@/components/grid/ScrollToTop';

// view components
import _AppGrid from '../components/grid/AppGrid.react';
import SearchPage from '../components/pages/search/SearchPage.react';
import ResultsPage from '../components/pages/results/ResultsPage.react';
import _HotelPage from '../components/pages/hotel/HotelPage.react';
import BookingsPage from '../components/pages/new-bookings/BookingsPage.react';
import RequestsPage from '../components/pages/requests/RequestsPage.react';
import ReportsPage from '../components/pages/reports/ReportsPage.react';
import UsersManagementPage from '../components/pages/users-management/UsersManagementPage.react';
import TravelPolicyPage from '../components/pages/travel_policy/TravelPolicyPage';
import AboutUsPage from '../components/pages/about-us/AboutUsPage.jsx';
import ContactUsPage from '../components/pages/contact-us/ContactUsPage';

import ReservationPaymentPage from '../components/pages/reservation-payment/ReservationPaymentPage.react';

import ManagementPage from '../components/pages/management/ManagementPage';
import _TripTablePage from '../components/pages/management/TripTablePage';
import _OngoingTripsPage from '../components/pages/management/OngoingTripsPage';
import _TripRequestPage from '../components/pages/management/TripRequestPage.react';
import _TripRequestWizardPage from '../components/pages/management/TripRequestWizardPage';
import _ForwardRequestsPage from '../components/pages/management/ForwardRequestsPage.react';
import _AddAirfareWizardPage from '../components/pages/management/AddAirfareWizardPage';
import PreviewPane from '../components/pages/management/PreviewPane';
import TripPreview from '../components/pages/management/TripPreview.react';

import EmptyPage from '../components/pages/EmptyPage.react';
import FaqPage from '../components/pages/FaqPage';
import NavigationTracker from '../components/NavigationTracker';
import { isIsracardCompany } from '@/utils/isCompany';
import Analytics from 'arbitrip-common/client/analytics';

const AppGrid = withRouter(_AppGrid);
const HotelPage = withRouter(_HotelPage);
const TripTablePage = withRouter(_TripTablePage);
const OngoingTripsPage = withRouter(_OngoingTripsPage);
const TripRequestPage = withRouter(_TripRequestPage);
const TripRequestWizardPage = withRouter(_TripRequestWizardPage);
const ForwardRequestsPage = withRouter(_ForwardRequestsPage);
const AddAirfareWizardPage = withRouter(_AddAirfareWizardPage);

export function isAnonymousBlocked(profile, permission) {
    const allowAnonymousUsers = Boolean(profile?.company?.settings?.allow_anonymous_users);
    const allowed = permission
        ? allowAnonymousUsers && Boolean(profile?.company?.settings?.anonymous_user_permissions?.[permission])
        : false;
    return Boolean(!profile?.isLoggedIn && !allowed);
}

export function redirectToLogin(profile) {
    const lang = profile?.language || '';
    const loginUrl = Config.is_letstay ? URLS.LETSTAY_LOGIN : URLS.LOGIN;
    const redirectUrl = `${loginUrl}?ref_url=${encodeURIComponent(window.location.href)}&lang=${lang}`;
    ProfileStore.clear();
    window.location.replace(addUtmSourceToUrl(redirectUrl));
}

function AnonymousGuard({ permission, children }) {
    const profile = ProfileStore.getProfile();
    const location = useLocation();
    const blocked = isAnonymousBlocked(profile, permission);

	useLayoutEffect(() => {
		if (blocked) {
			if (location.pathname.startsWith('/hotel/')) {
				Analytics.actions.views.hotelPageBlocked(profile);
			}
			redirectToLogin(profile);
		}
	}, [blocked]);

    if (blocked) return null;
    return children;
}

function HotelPageWrapper() {
    const { hotel_name, hotel_id } = useParams();
    return <HotelPage hotelName={hotel_name} hotelId={hotel_id} />;
}

function isStrictMode() {
    const strict_mode = (process.env.REACT_APP_STRICT_MODE || '').toLowerCase();

    return strict_mode === 'true';
}

function StrictModeWrapper(component) {
    console.warn('App is running is StrictMode...');
    return <React.StrictMode>{component}</React.StrictMode>;
}

function getSearchPath() {
    const searchParams = document.location.search;
    return `/search${searchParams}`;
}

function BlockIsracardFaqAbout({ children }) {
    const isIsracard = React.useSyncExternalStore(
        (onStoreChange) => {
            ProfileStore.addChangeListener(onStoreChange);
            return () => ProfileStore.removeChangeListener(onStoreChange);
        },
        () => isIsracardCompany(),
        () => false,
    );
    if (isIsracard) {
        return <Navigate to={getSearchPath()} replace />;
    }
    return children;
}

const BrowserRouterAppGrid = (
    <BrowserRouter>
        <NavigationTracker />
        <ScrollToTop />
        <AppGrid>
            <Routes>
                <Route path="/search" element={<SearchPage />} />

                <Route
                    path="/results"
                    element={
                        <AnonymousGuard permission="search_results">
                            <ResultsPage />
                        </AnonymousGuard>
                    }
                />

                <Route
                    path="/hotel/:hotel_name/:hotel_id"
                    element={
                        <AnonymousGuard permission="hotel_details">
                            <HotelPageWrapper />
                        </AnonymousGuard>
                    }
                />

                <Route
                    path="/reservation"
                    element={
                        <AnonymousGuard permission="perform_reservation">
                            <ReservationPaymentPage />
                        </AnonymousGuard>
                    }
                />

                <Route
                    path="/bookings"
                    element={
                        <AnonymousGuard>
                            <BookingsPage />
                        </AnonymousGuard>
                    }
                />
                <Route
                    path="/bookings/:booking_id"
                    element={
                        <AnonymousGuard>
                            <BookingsPage />
                        </AnonymousGuard>
                    }
                />
                <Route path="/requests" element={<RequestsPage />} />
                <Route path="/dashboard" element={<ReportsPage />} />
                <Route path="/management" element={<UsersManagementPage />} />
                <Route path="/travel_policy" element={<TravelPolicyPage />} />
                <Route
                    path="/faq"
                    element={
                        <BlockIsracardFaqAbout>
                            <FaqPage />
                        </BlockIsracardFaqAbout>
                    }
                />
                <Route
                    path="/about-us"
                    element={
                        <BlockIsracardFaqAbout>
                            <AboutUsPage />
                        </BlockIsracardFaqAbout>
                    }
                />
                <Route path="/contact-us" element={<ContactUsPage />} />

                <Route path="/management" element={<Navigate to="/trips" />} />

                <Route
                    path="/trips/live"
                    element={<ManagementPage main={<OngoingTripsPage />} sidebar={<TripPreview />} />}
                />
                <Route
                    path={'/' + ManagementConstants.SOURCES.TRIPS}
                    element={<ManagementPage main={<TripTablePage />} sidebar={<TripPreview />} />}
                />
                <Route
                    path="/trip/:trip_request_id"
                    element={<ManagementPage main={<TripRequestPage />} sidebar={<PreviewPane />} />}
                />
                <Route
                    path={'/' + ManagementConstants.SOURCES.WIZARD}
                    element={<ManagementPage main={<TripRequestWizardPage />} />}
                />
                <Route
                    path={'/' + ManagementConstants.SOURCES.FORWARD}
                    element={<ManagementPage main={<ForwardRequestsPage />} sidebar={<PreviewPane />} />}
                />
                <Route
                    path={'/' + ManagementConstants.SOURCES.ADD_AIRFARE_WIZARD}
                    element={<ManagementPage main={<AddAirfareWizardPage />} sidebar={<PreviewPane />} />}
                />

                <Route path="/unauthorized" element={<EmptyPage />} />

                <Route path="/" element={<Navigate to={getSearchPath()} />} />

                <Route path="*" element={<Navigate to={getSearchPath()} />} />
            </Routes>
        </AppGrid>
    </BrowserRouter>
);

function App() {
    return isStrictMode() ? StrictModeWrapper(BrowserRouterAppGrid) : BrowserRouterAppGrid;
}

export default App;
